import { PrototypeApiError } from "../apiClient";
import { byId, inputValue } from "../domUtils";
import type { PrototypeRuntime, Root } from "../prototypeTypes";
import { resetForgotTurnstileAfterError } from "../turnstileWidgetController";
import { setAuthButtonLoading } from "./authButtonLock";
import { clearAuthModalFields } from "../formProfileController";
import { validateEmail } from '../validation';

export async function handleForgotPassword(
	root: Root,
	runtime: PrototypeRuntime,
	handlers: { failValidation(message: string): false; recordRuntimeError(runtime: PrototypeRuntime, error: unknown): void },
) {
	const email = inputValue(root, "forgotEmail").trim();
	const emailError = validateEmail(email);
	if (emailError) return handlers.failValidation(emailError);
	if (!runtime.interactiveTurnstile?.hasToken()) return handlers.failValidation("Please complete the security verification first.");

	const button = root.querySelector<HTMLButtonElement>("#forgotSubmitBtn");
	const originalText = button?.textContent ?? undefined;
	const isAttemptCurrent = beginForgotAttempt(root, button);
	setAuthButtonLoading(button, true, originalText, 'Sending…');
	try {
		const turnstileToken = await runtime.interactiveTurnstile.getToken("auth-password-reset");
		if (!isAttemptCurrent()) return;
		await runtime.api.forgotPassword({ email, turnstileToken });
		if (!isAttemptCurrent()) return;
		const form = byId(root, "forgotForm");
		const success = byId(root, "forgotSuccess");
		if (form) form.hidden = true;
		if (success) success.hidden = false;
		clearAuthModalFields(root, 'forgot');
	} catch (error) {
		if (!isAttemptCurrent()) return;
		handlers.recordRuntimeError(runtime, error);
		if (error instanceof PrototypeApiError && error.status === 403) {
			const code = typeof (error.body as Record<string, unknown> | undefined)?.error === "string" ? (error.body as Record<string, string>).error : "";
			if (code === "turnstile_required" || code === "turnstile_failed") {
				resetForgotTurnstileAfterError(root, runtime);
				return handlers.failValidation("Security verification failed. Please complete the check and try again.");
			}
		}
		if (error instanceof PrototypeApiError && error.status === 400) return handlers.failValidation("Please enter a valid email address.");
		if (error instanceof PrototypeApiError && error.status === 503) return handlers.failValidation("Security verification is temporarily unavailable. Please try again in a moment.");
		if (error instanceof PrototypeApiError && error.status === 429) return handlers.failValidation("Too many reset requests. Please wait a moment before trying again.");
		return handlers.failValidation("Could not send reset link. Please try again.");
	} finally {
		if (isAttemptCurrent()) setAuthButtonLoading(button, false, originalText);
	}
}

function beginForgotAttempt(root: Root, button: HTMLButtonElement | null): () => boolean {
	if (!button) return () => false;
	const token = crypto.randomUUID();
	button.dataset.uiAttempt = token;
	return () => button.dataset.uiAttempt === token && root.querySelector('#forgotModal')?.classList.contains('active') === true;
}
